"use client";

import { motion } from "framer-motion";
import { useEffect } from "react";
import { Input } from "@/components/shadcn/input";
import { Label } from "@/components/shadcn/label";
import { CopyButton } from "@/components/UI/CopyButton";
import { useSpotUser } from "@/utils/spotUser";
import { EnvService } from "@/services/Environment/Environment";
import { StepLayout } from "./HowItWorksSteps";
import { InboxSource } from "../types";

interface SourceConfigStepProps {
  source: InboxSource | null;
  config: Record<string, any>;
  onChange: (config: Record<string, any>) => void;
  onNext: () => void;
  onBack: () => void;
  canProceed: boolean;
  slugError?: string | null;
}

export const SourceConfigStep = ({
  source,
  config,
  onChange,
  onNext,
  onBack,
  canProceed,
  slugError,
}: SourceConfigStepProps) => {
  const { spotTenant } = useSpotUser();
  const inboxDomain = EnvService.getVar("INBOX_DOMAIN") || "inbox.noxus.ai";

  useEffect(() => {
    if (source === "noxus" && config.slug === undefined && spotTenant?.name) {
      const defaultSlug = spotTenant.name
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, "")
        .slice(0, 30);
      onChange({ ...config, slug: defaultSlug });
    }
  }, [source, spotTenant, config, onChange]);

  if (source === "noxus") {
    return (
      <StepLayout
        onNext={onNext}
        onBack={onBack}
        disabled={!canProceed}
        nextVariant="outline"
      >
        <div className="text-center space-y-2 mb-6">
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.4 }}
            className="text-lg font-medium text-foreground"
          >
            Configure your Noxus inbox
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="text-sm text-muted-foreground max-w-[370px] mx-auto"
          >
            Choose a unique slug for your inbox email address.
          </motion.p>
        </div>

        <div className="w-full max-w-md space-y-4 pb-2 mx-auto text-left">
          <div className="space-y-2">
            <Label htmlFor="slug">Inbox slug</Label>
            <div className="relative">
              <Input
                id="slug"
                placeholder="e.g. support-acme"
                value={config.slug || ""}
                maxLength={30}
                onChange={(e) => {
                  const sanitized = e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9-]/g, "");
                  onChange({ ...config, slug: sanitized });
                }}
                className="pr-32"
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-muted-foreground text-sm border-l pl-3 bg-muted/30 rounded-r-md">
                @{inboxDomain}
              </div>
            </div>
            {slugError ? (
              <p className="text-xs text-destructive">{slugError}</p>
            ) : (
              <div className="flex items-center gap-1">
                <p className="text-xs text-muted-foreground">
                  Your inbox email will be:{" "}
                  <span className="font-medium text-foreground">
                    {config.slug || "your-slug"}@{inboxDomain}
                  </span>
                </p>
                <CopyButton
                  value={`${config.slug || "your-slug"}@${inboxDomain}`}
                  iconSize={10}
                />
              </div>
            )}
          </div>
        </div>
      </StepLayout>
    );
  }

  return (
    <StepLayout
      onNext={onNext}
      onBack={onBack}
      disabled={!canProceed}
      nextVariant="outline"
    >
      <div className="text-center py-20">
        <p className="text-muted-foreground">
          No configuration required for this source.
        </p>
      </div>
    </StepLayout>
  );
};
